import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ShoppingCart, Heart, Star, Eye, Zap, ArrowRight, Check, Loader } from 'lucide-react'
import { useProducts }  from '../hooks/useProducts'
import { useCart }      from '../contexts/CartContext'
import { useWishlist }  from '../contexts/WishlistContext'
import { useAuth }      from '../contexts/AuthContext'
import { useToast }     from '../contexts/ToastContext'
import { ProductCardSkeleton } from './Skeleton'
import { formatPrice as fmt, t } from '../utils/format'
import './FeaturedProducts.css'

/*
 | Il y avait ici un repli de six produits code en dur : « Salon Prestige 5
 | Places » a 4 200 000 GNF note 4,9 sur 128 avis, « Réfrigérateur Samsung
 | 500L », « Chaise Massage 4D Deluxe », chacun avec sa photo Unsplash, son
 | prix barre et son nombre d'avis. Aucun de ces six produits n'existe, aucun
 | de ces avis n'a ete ecrit, aucun de ces prix n'a ete pratique.
 |
 | Il s'affichait des que l'API tardait ou rendait une liste vide, c'est-a-dire
 | exactement dans les moments ou le site est en difficulte : un visiteur
 | pouvait cliquer sur un de ces produits et tomber sur une page 404, ou
 | pire, appeler un magasin pour commander un article qui n'a jamais ete au
 | catalogue.
 |
 | Le vide s'annonce maintenant comme un vide.
 */

const TABS = [
  { key: 'all',          fr: 'Tous',              en: 'All'         },
  { key: 'salons',       fr: 'Salons',            en: 'Living Room' },
  { key: 'chambres',     fr: 'Chambres',          en: 'Bedrooms'    },
  { key: 'decoration',   fr: 'Décoration',        en: 'Decoration'  },
  { key: 'electromenager',fr:'Électroménager',    en: 'Appliances'  },
  { key: 'massage',      fr: 'Massage',           en: 'Massage'     },
  { key: 'sport',        fr: 'Sport',             en: 'Sport'       },
]

function ProductCard({ product, lang }) {
  const [adding,  setAdding]  = useState(false)
  const [added,   setAdded]   = useState(false)
  const { addToCart }                       = useCart()
  const { toggle: toggleWishlist, isInWishlist } = useWishlist()
  const { isAuth }                 = useAuth()
  const { addToast }                        = useToast()
  const navigate                            = useNavigate()
  const liked = isInWishlist(product.id)

  const handleWishlist = useCallback(async (e) => {
    e.preventDefault()
    if (!isAuth) { navigate('/auth/login'); return }
    await toggleWishlist(product)
  }, [isAuth, product, toggleWishlist, navigate])

  const handleAddToCart = async (e) => {
    e.preventDefault()
    if (adding || added) return
    setAdding(true)
    try {
      await addToCart(product, 1)
      setAdded(true)
      setTimeout(() => setAdded(false), 2000)
    } catch (err) {
      /*
       | L'echec etait avale : le bouton repassait de « ajout... » a son etat
       | initial sans coche ni message. Le client concluait a un clic rate et
       | recommencait, sur la page qui porte la mise en avant produit.
       */
      addToast(err?.message ?? "L'article n'a pas pu etre ajoute au panier.", 'error')
    }
    finally { setAdding(false) }
  }

  const badgeMap = {
    hot:  { label: 'Bestseller', cls: 'badge-hot'  },
    new:  { label: lang === 'fr' ? 'Nouveau' : 'New', cls: 'badge-new' },
    sale: { label: `-${product.discount_percent}%`, cls: 'badge-sale' },
  }
  const badge = badgeMap[product.badge_type]

  return (
    <Link to={`/produits/${product.slug}`} className="product-card">
      {/* Image */}
      <div className="product-img-wrap">
        <img src={product.primary_image} alt={t(product.name, lang)} className="product-img" />

        {product.is_flash_active
          ? <span className="product-badge badge-flash"><Zap size={10} />Flash</span>
          : badge && (
            <span className={`product-badge ${badge.cls}`}>
              {product.badge_type === 'hot' && <Zap size={10} />}
              {badge.label}
            </span>
          )
        }

        <div className="product-actions">
          <button
            className={`product-action-btn ${liked ? 'liked' : ''}`}
            onClick={handleWishlist}
            aria-label="Favoris"
          >
            <Heart size={16} fill={liked ? 'currentColor' : 'none'} />
          </button>
          <button className="product-action-btn" aria-label="Aperçu" onClick={e => e.preventDefault()}>
            <Eye size={16} />
          </button>
        </div>

        <div className="product-cart-overlay" onClick={e => e.preventDefault()}>
          <button
            className={`btn btn-primary ${added ? 'cart-added' : ''}`}
            style={{ width:'100%', justifyContent:'center' }}
            onClick={handleAddToCart}
            disabled={adding || product.stock === 0}
          >
            {adding  ? <Loader  size={15} className="cart-spin" /> :
             added   ? <Check   size={15} /> :
                       <ShoppingCart size={15} />}
            {added
              ? (lang === 'fr' ? 'Ajouté !' : 'Added!')
              : (lang === 'fr' ? 'Ajouter au panier' : 'Add to cart')}
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="product-info">
        <span className="product-category">{t(product.category?.name, lang)}</span>
        <h3 className="product-name">{t(product.name, lang)}</h3>

        {/*
          | Sans avis, pas d'etoiles. La table des avis est vide : chaque
          | fiche affichait cinq etoiles grises suivies de « 0 (0) », ce qui
          | se lit comme un produit mal note et non comme un produit
          | nouveau.
        */}
        {product.rating_count > 0 && (
          <div className="product-rating">
            <div className="stars">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={12}
                  fill={i < Math.floor(product.rating) ? '#FFB800' : 'none'}
                  color={i < Math.floor(product.rating) ? '#FFB800' : '#D1D5DB'}
                />
              ))}
            </div>
            <span className="rating-score">{product.rating}</span>
            <span className="rating-count">({product.rating_count})</span>
          </div>
        )}

        <div className="product-price-row">
          <div className="product-prices">
            <span className="product-price">
              {fmt(product.is_flash_active ? product.flash_sale_price : product.price)}
            </span>
            {product.is_flash_active
              ? <span className="product-old-price">{fmt(product.price)}</span>
              : product.old_price && <span className="product-old-price">{fmt(product.old_price)}</span>
            }
          </div>
          <button
            className={`cart-btn-sm btn btn-primary ${added ? 'cart-added' : ''}`}
            aria-label="Ajouter au panier"
            onClick={handleAddToCart}
            disabled={adding || product.stock === 0}
          >
            {adding ? <Loader size={14} className="cart-spin" /> :
             added  ? <Check  size={14} /> :
                      <ShoppingCart size={16} />}
          </button>
        </div>
      </div>
    </Link>
  )
}

/**
 * Le compte a rebours d'une vente flash reelle.
 *
 * Il visait minuit du jour suivant, tous les jours, indefiniment, sous un
 * bandeau « Vente Flash, jusqu'à -40% sur une sélection ». Aucune vente
 * flash n'etait en cours : la remise n'existait pas, l'echeance non plus, et
 * l'horloge recommencait chaque nuit. C'est le genre d'urgence fabriquee
 * qu'un client remarque au deuxieme passage, et qui jette un doute sur tous
 * les autres prix de la page.
 *
 * Il prend maintenant une echeance en parametre et rend null quand il n'y en
 * a pas : sans vente flash, il n'y a pas de bandeau du tout.
 *
 * L'echeance est un horodatage en millisecondes, pas un objet Date : une Date
 * reconstruite a chaque rendu changerait d'identite a chaque fois et
 * relancerait l'effet en boucle.
 *
 * @param {number|null} echeance
 */
function useCompteARebours(echeance) {
  const [reste, setReste] = useState(null)

  useEffect(() => {
    if (! echeance) { setReste(null); return }

    const calcul = () => {
      const diff = Math.max(0, echeance - Date.now())

      return {
        h: Math.floor(diff / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
        fini: diff === 0,
      }
    }

    setReste(calcul())
    const id = setInterval(() => setReste(calcul()), 1000)

    return () => clearInterval(id)
  }, [echeance])

  return reste
}

export default function FeaturedProducts({ lang }) {
  const [activeTab, setActiveTab] = useState('all')
  const { products, loading, fetch } = useProducts()

  /*
   | Le chargement initial. Il manquait, et la section etait morte.
   |
   | useProducts a cesse de charger au montage le jour ou deux requetes
   | concurrentes ont fait apparaitre tout le catalogue sur une page filtree.
   | La correction etait juste, mais ce composant-ci lui passait ses
   | parametres en argument — un argument que le hook ignore depuis. Plus
   | personne n'appelait fetch tant qu'on ne cliquait pas sur un onglet.
   |
   | Mesure faite sur le site en production, pas deduite : la grille de
   | « Nos coups de cœur » contient six squelettes gris et zero carte
   | produit, a chaque visite. Le repli code en dur ne la sauvait pas non
   | plus, puisque la grille rend des squelettes tant que loading est vrai,
   | et loading ne redescendait jamais.
   */
  useEffect(() => {
    fetch({ featured: true, per_page: 6 })
  }, [fetch])

  /*
   | Le filtrage local ne sert que le rendu de l'onglet actif : handleTab
   | relance de toute facon un appel filtre cote serveur.
   */
  const filtered = activeTab === 'all'
    ? products
    : products.filter(p => p.category?.slug === activeTab || t(p.category?.name, 'fr')?.toLowerCase() === activeTab)

  /*
   | La vente flash est lue dans les donnees, jamais annoncee d'avance.
   |
   | L'echeance retenue est la plus proche : c'est celle qui expire en
   | premier, donc la seule que le compte a rebours peut afficher sans
   | mentir. La remise affichee est la plus forte en cours, arrondie vers le
   | bas pour qu'un « jusqu'a -25 % » ne promette jamais plus que ce que le
   | panier appliquera.
   */
  const enFlash = products.filter(
    (p) => p.is_flash_active && p.flash_sale_ends_at && p.flash_sale_price > 0,
  )

  const echeance = enFlash.length
    ? Math.min(...enFlash.map((p) => new Date(p.flash_sale_ends_at).getTime()))
    : null

  /*
   | Le calcul se fait en entiers, et ce n'est pas du purisme.
   |
   | Ecrit (1 - prix_flash / prix) * 100, une remise de 20 % exacte
   | (2 800 000 sur 3 500 000) donne 19,999999999999996 en virgule flottante,
   | et le plancher rend 19. Le bandeau annoncait un point de moins que la
   | remise reellement appliquee au panier. Le test de bout en bout l'a
   | attrape ; a la lecture du code, rien ne le laissait voir.
   |
   | Les prix sont en francs guineens entiers, tres loin de la limite des
   | entiers surs : la multiplication d'abord, la division ensuite.
   */
  const remiseMax = enFlash.length
    ? Math.max(...enFlash.map((p) => Math.floor(((p.price - p.flash_sale_price) * 100) / p.price)))
    : 0

  const timer = useCompteARebours(echeance)

  const handleTab = (key) => {
    setActiveTab(key)
    if (key === 'all') {
      fetch({ featured: true, per_page: 6 })
    } else {
      fetch({ category: key, per_page: 6 })
    }
  }

  return (
    <section className="featured-products" id="products">
      <div className="fp-inner">
        <div className="fp-header reveal">
          <div>
            {/*
              | Plus de pastille de section ici : l'ouverture en porte une,
              | et « Nos coups de coeur » dit deja de quoi il s'agit.
              |
              | L'accroche annoncait « les produits les plus appréciés par nos
              | clients guinéens ». Il n'y a aucun avis en base : ce n'etait
              | pas une exageration, c'etait un classement sans classement.
            */}
            <h2 className="section-title">
              {lang === 'fr' ? 'Nos coups de' : 'Our top'} <span>{lang === 'fr' ? 'cœur' : 'picks'}</span>
            </h2>
            <p className="section-subtitle">
              {lang === 'fr'
                ? 'Les articles que nous mettons en avant en ce moment, disponibles en magasin et à la livraison.'
                : 'The items we are featuring right now, available in store and for delivery.'}
            </p>
          </div>
          <Link to="/catalogue" className="btn btn-blue fp-header-cta">
            {lang === 'fr' ? 'Voir tout' : 'View all'}
            <ArrowRight size={16} />
          </Link>
        </div>

        {/* Tabs */}
        <div className="fp-tabs reveal">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`fp-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => handleTab(tab.key)}
            >
              {lang === 'fr' ? tab.fr : tab.en}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading || filtered.length > 0 ? (
          <div className="products-grid">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <ProductCardSkeleton key={i} />)
              : filtered.map((product, i) => (
                <div key={product.id} className={`reveal delay-${Math.min(i + 1, 6)}`}>
                  <ProductCard product={product} lang={lang} />
                </div>
              ))}
          </div>
        ) : (
          <div className="fp-vide reveal">
            <p className="fp-vide-titre">
              {lang === 'fr'
                ? 'Rien à afficher dans ce rayon pour le moment.'
                : 'Nothing to show in this department yet.'}
            </p>
            <p className="fp-vide-note">
              {lang === 'fr'
                ? 'Le stock arrive rayon par rayon. Le catalogue complet reste accessible.'
                : 'Stock arrives department by department. The full catalog stays available.'}
            </p>
            <Link to="/catalogue" className="btn btn-blue">
              {lang === 'fr' ? 'Voir le catalogue' : 'View the catalog'}
              <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {/*
          | Le bandeau n'apparait que si une vente flash court vraiment, et
          | disparait a la seconde ou elle se termine. Il etait auparavant
          | permanent, avec une horloge qui repartait chaque nuit.
        */}
        {timer && ! timer.fini && (
          <div className="flash-sale reveal">
            <div className="flash-sale-left">
              <Zap size={24} fill="currentColor" />
              <div>
                <span className="flash-title">{lang === 'fr' ? 'Vente Flash' : 'Flash Sale'}</span>
                <span className="flash-sub">
                  {lang === 'fr'
                    ? `Jusqu'à -${remiseMax}% sur ${enFlash.length} article${enFlash.length > 1 ? 's' : ''}`
                    : `Up to -${remiseMax}% on ${enFlash.length} item${enFlash.length > 1 ? 's' : ''}`}
                </span>
              </div>
            </div>
            <div className="flash-timer">
              <div className="timer-block"><span>{String(timer.h).padStart(2,'0')}</span><small>h</small></div>
              <span className="timer-sep">:</span>
              <div className="timer-block"><span>{String(timer.m).padStart(2,'0')}</span><small>min</small></div>
              <span className="timer-sep">:</span>
              <div className="timer-block"><span>{String(timer.s).padStart(2,'0')}</span><small>sec</small></div>
            </div>
            <Link to="/catalogue" className="btn btn-primary flash-cta">
              {lang === 'fr' ? 'Voir les offres' : 'See offers'}
            </Link>
          </div>
        )}
      </div>
    </section>
  )
}
